import { useMemo, useState } from 'react';
import { SECOND_IN_MS, TEN_MINUTES_IN_MS } from '@packages/shared/core/common/constants';
import { debounce } from '@packages/shared/core/common/utils/debounce';
import { useSuggestedLocationsQuery } from '../queries';

const DEBOUNCE_TIMEOUT_MS = SECOND_IN_MS / 3;

export const useSuggestLocation = () => {
  const [searched, setSearched] = useState('');

  const debouncedSetSearchedValue = useMemo(
    () => debounce((value: string) => setSearched(value), DEBOUNCE_TIMEOUT_MS),
    [],
  );

  const calculatedKey = searched ? `location-suggestions-${searched}` : null;

  const { data } = useSuggestedLocationsQuery({
    key: calculatedKey,
    location: searched,
    options: {
      fallback: {
        data: [],
      },
      dedupingInterval: TEN_MINUTES_IN_MS,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });

  const formattedSuggestions = data.map((location) => {
    const cityName = location.properties.name ? `${location.properties.name}, ` : '';
    const regionName = location.properties.region ? `${location.properties.region}, ` : '';
    const countryName = location.properties.country ?? '';
    const label = `${cityName}${regionName}${countryName}`;

    return {
      id: location.properties.id,
      label,
      cityName: location.properties.name ?? '',
      coordinates: location.geometry.coordinates.map((coordinate: number) => coordinate.toString()),
    };
  });

  return {
    suggestedData: formattedSuggestions,
    debouncedSetSearchedValue,
  };
};
